import { useCallback, useMemo, useRef, useState } from 'react'
import { ApiError, recipientApi, type Section, type StoredFile } from '../lib/api'
import { uploadFile, type UploadProgress } from '../lib/uploader'
import { formatBytes, previewKind } from '../lib/format'
import { ProgressBar, Spinner, EmptyState } from './ui'
import { PreviewGrid } from './PreviewGrid'

interface ActiveUpload {
  key: string
  name: string
  progress: UploadProgress
  controller: AbortController
}

interface Props {
  token: string
  section: Section
  files: StoredFile[]
  /** Bytes still available across the whole dropbox — checked before we start. */
  freeBytes: number
  /** Slots left across the whole dropbox, not just this section. */
  freeSlots: number
  readOnly: boolean
  isAdmin: boolean
  onChanged: () => void
}

export function FileSection({
  token,
  section,
  files,
  freeBytes,
  freeSlots,
  readOnly,
  isAdmin,
  onChanged,
}: Props) {
  const [active, setActive] = useState<ActiveUpload[]>([])
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const atSectionLimit =
    section.maxFiles !== null && files.length + active.length >= section.maxFiles
  // The dropbox-wide cap bites before any section's own limit does.
  const atDropboxLimit = freeSlots - active.length <= 0
  const atFileLimit = atSectionLimit || atDropboxLimit

  const start = useCallback(
    async (picked: FileList | File[]) => {
      setError(null)
      const list = Array.from(picked)
      if (list.length === 0) return

      // Reject what obviously won't fit before touching the network, so the user
      // gets an instant answer instead of a 413 after a round trip.
      let budget = freeBytes - active.reduce((n, a) => n + a.progress.totalBytes, 0)
      const tooBig = list.find((f) => f.size > budget)
      if (tooBig) {
        setError(
          `"${tooBig.name}" (${formatBytes(tooBig.size)}) does not fit — only ${formatBytes(
            Math.max(0, budget),
          )} left in this dropbox.`,
        )
        return
      }
      budget -= list.reduce((n, f) => n + f.size, 0)

      for (const file of list) {
        const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
        const controller = new AbortController()

        setActive((prev) => [
          ...prev,
          {
            key,
            name: file.name,
            controller,
            progress: { state: 'queued', progress: 0, uploadedBytes: 0, totalBytes: file.size },
          },
        ])

        try {
          await uploadFile({
            token,
            sectionId: section.id,
            file,
            signal: controller.signal,
            onProgress: (progress) =>
              setActive((prev) => prev.map((a) => (a.key === key ? { ...a, progress } : a))),
          })
        } catch (err) {
          if (!(err instanceof DOMException && err.name === 'AbortError')) {
            setError(err instanceof ApiError ? err.message : (err as Error).message)
          }
        } finally {
          setActive((prev) => prev.filter((a) => a.key !== key))
          onChanged()
        }
      }
    },
    [active, freeBytes, onChanged, section.id, token],
  )

  const remove = async (file: StoredFile) => {
    if (!window.confirm(`Remove "${file.filename}"? This deletes the uploaded file.`)) return
    try {
      await recipientApi.abortUpload(token, file.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message)
    }
    onChanged()
  }

  const downloadUrl = (fileId: string, inline = false) => {
    const base = isAdmin
      ? `/api/a/${token}/files/${fileId}/download`
      : recipientApi.downloadUrl(token, fileId)
    return inline ? `${base}?inline=1` : base
  }

  const thumbUrl = (fileId: string) =>
    isAdmin ? `/api/a/${token}/files/${fileId}/thumb` : recipientApi.thumbUrl(token, fileId)

  // Images and PDFs get thumbnail cards; everything else stays a filename row.
  const [previewable, others] = useMemo(() => {
    const shown: StoredFile[] = []
    const rest: StoredFile[] = []
    for (const f of files) {
      const canPreview = f.status === 'complete' && previewKind(f.contentType, f.filename) !== null
      ;(canPreview ? shown : rest).push(f)
    }
    return [shown, rest]
  }, [files])

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            if (!atFileLimit) void start(e.dataTransfer.files)
          }}
          onClick={() => !atFileLimit && inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragging
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-700/10'
              : 'border-zinc-300 hover:border-brand-400 dark:border-zinc-700'
          } ${atFileLimit ? 'pointer-events-none opacity-50' : ''}`}
        >
          <div className="text-sm font-medium">
            {atDropboxLimit
              ? 'This dropbox is full'
              : atSectionLimit
                ? 'File limit reached for this section'
                : 'Drop files here or click to browse'}
          </div>
          <div className="mt-1 text-xs text-zinc-500">
            {formatBytes(freeBytes)} left
            {freeSlots > 0 && ` · ${freeSlots} file slot(s)`}
            {section.maxFiles !== null && ` · up to ${section.maxFiles} in this section`}
            {section.accept.length > 0 && ` · ${section.accept.join(', ')}`}
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple={section.maxFiles !== 1}
            accept={section.accept.length ? section.accept.join(',') : undefined}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void start(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {active.map((a) => (
        <div key={a.key} className="rounded-xl border border-zinc-200 px-3.5 py-3 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <Spinner className="size-4 text-brand-500" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="text-xs text-zinc-500">
                {formatBytes(a.progress.uploadedBytes)} of {formatBytes(a.progress.totalBytes)} ·{' '}
                {Math.round(a.progress.progress * 100)}%
              </div>
            </div>
            <button
              type="button"
              onClick={() => a.controller.abort()}
              className="text-xs font-semibold text-zinc-500 hover:text-red-600"
            >
              Cancel
            </button>
          </div>
          <div className="mt-2">
            <ProgressBar
              value={a.progress.progress}
              tone={a.progress.state === 'error' ? 'danger' : 'brand'}
            />
          </div>
          {a.progress.error && (
            <div className="mt-1.5 text-xs text-red-600 dark:text-red-400">{a.progress.error}</div>
          )}
        </div>
      ))}

      {files.length === 0 && active.length === 0 && readOnly && (
        <EmptyState title="No files were uploaded here" />
      )}

      {previewable.length > 0 && (
        <PreviewGrid
          files={previewable}
          urlFor={downloadUrl}
          thumbFor={thumbUrl}
          readOnly={readOnly}
          onRemove={(file) => void remove(file)}
          onAwaitingThumb={onChanged}
        />
      )}

      {others.length > 0 && (
        <ul className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
          {others.map((file) => (
            <li key={file.id} className="flex items-center gap-3 px-3.5 py-2.5">
              <FileGlyph />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{file.filename}</div>
                <div className="text-xs text-zinc-500">
                  {formatBytes(file.sizeBytes)}
                  {file.status === 'pending' && ' · incomplete'}
                </div>
              </div>
              {file.status === 'complete' && (
                <a href={downloadUrl(file.id)} className="text-xs font-semibold text-brand-600 hover:underline">
                  Download
                </a>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => void remove(file)}
                  data-destructive
                  className="text-xs font-semibold text-zinc-400 hover:text-red-600"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FileGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-5 shrink-0 text-zinc-400" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M14 3v5h5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" strokeLinejoin="round" />
    </svg>
  )
}
